import { useCallback, useEffect, useRef } from 'react'
import { useTrackerConfig } from '../src/components'
import type { ExposureKey, TrackerConfig } from '@avia/tracker'

export function useExposureObserver(data?: TrackerConfig) {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observed = useRef<Map<ExposureKey, TrackerConfig>>(new Map())

  const [, mergeConfig] = useTrackerConfig(data)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target
          const trackerData = mergeConfig(observed.current.get(target))
          if (entry.isIntersecting) {
            tracker.exposure({ ...trackerData }, target)
          } else {
            tracker.exposure({}, target, false)
          }
        })
      },
      {
        threshold: 0.8,
      },
    )

    Array.from(observed.current.keys()).forEach((item) => {
      observerRef.current?.observe(item as HTMLElement)
    })

    return () => {
      observerRef.current?.disconnect()

      Array.from(observed.current.keys()).forEach((item) => {
        tracker.exposure({}, item as unknown as string, false)
      })
    }
  }, [])

  return useCallback(
    (element: HTMLElement | null, data = {} as TrackerConfig, bind = true) => {
      if (!element) {
        return
      }
      if (bind) {
        observerRef.current?.observe(element)
        observed.current.set(element, data)
        return
      }
      observerRef.current?.unobserve(element)
      observed.current.delete(element)
    },
    [observerRef],
  )
}
